// 🧩 지그 사용량·요금 구간 공용 (2026-10-23) — 지그 화면(Jig.js)·구독 화면(JigUsagePlan.js)이 같은 숫자를 보게 한 곳에 둔다.
// 사용량 = 이번 달 완성(성공) 지그 개수. 집계 소스 = jigJobs(status done/delivered) ∪ jigResults(완성본). 실패(error)는 제외(미과금).

// 요금 구간(예정 요금 안내 전용 — 과금 아님). 2026-10-28 대표 확정안 — 정본 docs/요금표_지그모델_자동생성_2026-10-28.md
//   단위: 지그 = 커스텀 치아 1개, 모델 = 상·하악 세트 1개. 부가세 포함. 초과 요금 없음(한도 도달 시 다음 구간 구독). 3,000개 초과 = 별도 계약.
//   hours = 1개당 5분으로 환산한 절약 시간(구간 시작~끝), days = 하루 8시간 기준.
export const JIG_TIERS = [
    { min: 0,    max: 49,   label: '0~49개',        price: '무료',        hours: '~4시간',     days: '~반나절' },
    { min: 50,   max: 99,   label: '50~99개',       price: '27,500원',    hours: '4~8시간',    days: '반나절~1일' },
    { min: 100,  max: 199,  label: '100~199개',     price: '55,000원',    hours: '8~17시간',   days: '1~2일' },
    { min: 200,  max: 399,  label: '200~399개',     price: '110,000원',   hours: '17~33시간',  days: '2~4일' },
    { min: 400,  max: 699,  label: '400~699개',     price: '220,000원',   hours: '33~58시간',  days: '4~7일' },
    { min: 700,  max: 1199, label: '700~1,199개',   price: '385,000원',   hours: '58~100시간', days: '7~12.5일' },
    { min: 1200, max: 1999, label: '1,200~1,999개', price: '660,000원',   hours: '100~167시간', days: '12.5~21일' },
    { min: 2000, max: 3000, label: '2,000~3,000개', price: '1,100,000원', hours: '167~250시간', days: '21~31일' },
];
export const JIG_TIER_OVER_NOTE = '3,000개 초과는 별도 계약';

export function jigTierFor(done) {
    return JIG_TIERS.find(t => done <= t.max) || JIG_TIERS[JIG_TIERS.length - 1];
}

function toDate(ts) {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (ts instanceof Date) return ts;
    if (typeof ts === 'number') return new Date(ts);
    return null;
}

// 상태(구분) 판정 — jigResults 있으면 완성, 없으면 jigJobs.status 로
export function deriveJigStatus(row) {
    // 실패 건 환자정보 보관 문서(jigResults.failed=true, 파일 없음 — 2026-10-24)는 완성이 아니다.
    //   다시 시도로 작업이 pending/claimed 가 됐으면 그 상태를, 아니면 '실패'.
    if (row.failed) {
        const s = row.status;
        return (s === 'pending' || s === 'claimed') ? s : 'error';
    }
    if (row._hasResult) return 'done';
    const s = row.status;
    if (s === 'error') return 'error';
    if (s === 'claimed') return 'claimed';
    if (s === 'done' || s === 'delivered') return 'done';
    return 'pending';
}

// 병합: jobId 기준(jigResults 가 완성·다운로드·환자정보를 덮어씀). 정렬은 호출 쪽에서.
export function mergeJigRows(jobs, results) {
    const map = {};
    jobs.forEach(j => {
        const k = j.jobId || j.id;
        map[k] = { ...j, jobId: k, _hasResult: false };
    });
    results.forEach(r => {
        const k = r.jobId || r.id;
        // failed 문서(실패 건 환자정보만)는 받을 파일이 없으므로 _hasResult=false — 받기 버튼·사진이 뜨지 않게
        map[k] = { ...(map[k] || {}), ...r, jobId: k, _hasResult: !r.failed };
    });
    return Object.values(map).map(row => ({ ...row, _status: deriveJigStatus(row) }));
}

// 이번 달 사용량 — 지그 카운트 = 케이스가 아니라 **치식(커스텀 어버트먼트 개수)** 기준 (대표 2026-10-22)
export function monthlyJigUsage(rows, now = new Date()) {
    const y = now.getFullYear(), mo = now.getMonth();
    const inThisMonth = (ts) => {
        const d = toDate(ts);
        return d && d.getFullYear() === y && d.getMonth() === mo;
    };
    const teethCount = (row) => {
        const n = Array.isArray(row.teeth) ? row.teeth.length : 0;
        return n > 0 ? n : 1;   // 치식 미상(만료·실패 등)은 최소 1개로
    };
    let done = 0, attempts = 0, failed = 0;
    rows.forEach(row => {
        // CNC 외주 건(channel 'cnc_outsource' — 서버가 외주 의뢰 검증 후 붙인 꼬리표)은 제조사 서비스라 기공소 사용량에서 뺀다 (대표 2026-10-24, D11)
        if (row.channel === 'cnc_outsource') return;
        // 집계 기준일: 완성=doneAt(없으면 createdAt), 그 외=createdAt
        const refTs = (row._status === 'done' && (row.doneAt || row.createdAt)) || row.createdAt;
        if (!inThisMonth(refTs)) return;
        const c = teethCount(row);
        attempts += c;
        if (row._status === 'done') done += c;
        else if (row._status === 'error') failed += c;
    });
    return { done, attempts, failed };
}
